/**
 * Optimistic overlay: the confirmed server snapshot plus every op still in the outbox,
 * applied with the same domain rules the server uses. Applying an op the server has
 * already confirmed is a no-op, so the overlay is safe whichever arrives first — the
 * live event or the /ops response.
 */
import type { PlanItemDTO, PlanSnapshot } from '@clear/contracts';
import { GATES, validateGateInput } from '@clear/domain';
import type { QueuedOp } from './outbox';

function patchItem(
  items: PlanItemDTO[],
  id: string,
  fn: (i: PlanItemDTO) => PlanItemDTO | null,
): PlanItemDTO[] {
  let changed = false;
  const out = items.map((i) => {
    if (i.id !== id) return i;
    const next = fn(i);
    if (!next) return i;
    changed = true;
    return next;
  });
  return changed ? out : items;
}

export function applyPending(snap: PlanSnapshot, queue: readonly QueuedOp[]): PlanSnapshot {
  if (!queue.length) return snap;
  let plan = snap.plan;
  let items = snap.items;
  for (const { op, userId, prev } of queue) {
    switch (op.type) {
      case 'gate.set':
      case 'gate.bulkOk': {
        const entries = op.type === 'gate.set' ? [{ itemId: op.itemId, entryId: op.entryId }] : op.entries;
        for (const e of entries) {
          items = patchItem(items, e.itemId, (i) => {
            if (i.gates[op.gate]?.entryId === e.entryId) return null;
            const input =
              op.type === 'gate.set'
                ? { status: op.status, okQty: op.okQty ?? null, reasonId: op.reasonId ?? null }
                : { status: 'ok' as const };
            const v = validateGateInput(i, op.gate, input);
            if (!v.ok) return null;
            const status = op.type === 'gate.set' ? op.status : 'ok';
            return {
              ...i,
              gates: {
                ...i.gates,
                [op.gate]: {
                  entryId: e.entryId,
                  status,
                  okQty: v.okQty,
                  reasonId: v.reasonId,
                  userId,
                  at: op.at,
                },
              },
            };
          });
        }
        break;
      }
      case 'gate.undo':
        for (const entryId of op.entryIds) {
          items = items.map((i) => {
            const g = GATES.find((x) => i.gates[x]?.entryId === entryId);
            if (!g) return i;
            const before = prev?.[entryId] ?? null;
            const gates = { ...i.gates };
            if (before) gates[g] = before;
            else delete gates[g];
            return { ...i, gates };
          });
        }
        break;
      case 'item.start':
        items = patchItem(items, op.itemId, (i) =>
          i.startedAt ? null : { ...i, startedAt: op.at, startedBy: userId },
        );
        break;
      case 'item.unstart':
        items = patchItem(items, op.itemId, (i) => ({ ...i, startedAt: null, startedBy: null }));
        break;
      case 'plan.create':
        if (!plan && snap.lineId === op.lineId && snap.date === op.date && snap.shiftId === op.shiftId) {
          plan = {
            id: op.planId,
            lineId: op.lineId,
            date: op.date,
            shiftId: op.shiftId,
            status: 'draft',
            version: 0,
            createdAt: op.at,
            createdBy: userId,
            publishedAt: null,
            publishedBy: null,
          };
        }
        break;
      case 'plan.publish':
        if (plan && plan.id === op.planId && plan.status === 'draft') {
          plan = { ...plan, status: 'published', publishedAt: op.at, publishedBy: userId };
          items = items.map((i) => ({ ...i, activeSince: op.at }));
        }
        break;
      case 'item.add':
        if (plan && plan.id === op.planId && !items.some((i) => i.id === op.itemId)) {
          const maxOrder = items.reduce((m, i) => Math.max(m, i.runOrder), 0);
          items = [
            ...items,
            {
              id: op.itemId,
              planId: op.planId,
              modelId: op.modelId,
              qty: op.qty,
              priority: op.priority,
              runOrder: maxOrder + 1,
              cancelled: false,
              startedAt: null,
              startedBy: null,
              activeSince: plan.status === 'published' ? op.at : 0,
              addedAt: op.at,
              addedBy: userId,
              gates: {},
            },
          ];
        }
        break;
      case 'item.update':
        items = patchItem(items, op.itemId, (i) => ({
          ...i,
          ...(op.qty !== undefined ? { qty: op.qty } : {}),
          ...(op.priority !== undefined ? { priority: op.priority } : {}),
        }));
        break;
      case 'item.reorder':
        if (plan && plan.id === op.planId) {
          const pos = new Map(op.order.map((id, i) => [id, i + 1]));
          const rest = items.filter((i) => !pos.has(i.id)).sort((a, b) => a.runOrder - b.runOrder);
          rest.forEach((i, k) => pos.set(i.id, op.order.length + k + 1));
          items = items.map((i) => ({ ...i, runOrder: pos.get(i.id)! }));
        }
        break;
      case 'item.cancel':
        items = patchItem(items, op.itemId, (i) => ({ ...i, cancelled: true }));
        break;
    }
  }
  return plan === snap.plan && items === snap.items ? snap : { ...snap, plan, items };
}
